import React, { useState, useEffect } from 'react';
import { X } from 'lucide-react';
import { Unit } from '../types/unit';
import { ColorCollectionUnit } from '../types/colorCollectionUnit';
import api from '../services/api';
import '../styles/AddModelsModal.css';

interface AddUnitsModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: (units: Array<{ unitId: string; isUpgrade: boolean; cost: number; price: number }>) => void;
  workspaceId: string;
  projectId: string;
  existingUnits?: ColorCollectionUnit[];
  editMode?: boolean;
}

interface UnitRow extends Unit {
  isUpgrade: boolean;
  cost: number;
  price: number;
  isSelected: boolean;
}

const AddUnitsModal: React.FC<AddUnitsModalProps> = ({
  isOpen,
  onClose,
  onSave,
  workspaceId,
  projectId,
  existingUnits = [],
  editMode = false
}) => {
  const [units, setUnits] = useState<UnitRow[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [filters, setFilters] = useState({
    status: '',
    bed: '',
    floor: ''
  });
  const [bulkCost, setBulkCost] = useState<string>('0');
  const [bulkPrice, setBulkPrice] = useState<string>('0');

  useEffect(() => {
    if (isOpen && projectId) {
      fetchUnits();
    }
  }, [isOpen, projectId]);

  const fetchUnits = async () => {
    try {
      setLoading(true);
      setError('');

      const response = await api.get(
        `/workspaces/${workspaceId}/units`,
        {
          params: {
            projectId: projectId,
            limit: 1000,
            sortBy: 'unitNo',
            sortOrder: 'asc'
          }
        }
      );

      const allUnits = response.data.units || [];

      // Map units to rows with selection state and pricing
      const unitRows: UnitRow[] = allUnits.map((unit: Unit) => {
        const existingUnit = existingUnits.find(eu => eu.unitId === unit._id);

        return {
          ...unit,
          isUpgrade: existingUnit?.isUpgrade || false,
          cost: existingUnit?.cost || 0,
          price: existingUnit?.price || 0,
          isSelected: editMode ? !!existingUnit : false
        };
      });

      // Filter based on mode
      if (editMode) {
        // In edit mode, show only units that are already added
        const filteredRows = unitRows.filter(row => row.isSelected);
        setUnits(filteredRows);
      } else {
        // In add mode, exclude units that are already added
        const existingUnitIds = new Set(existingUnits.map(eu => eu.unitId));
        const filteredRows = unitRows.filter(row => !existingUnitIds.has(row._id));
        setUnits(filteredRows);
      }
    } catch (err: any) {
      console.error('Error fetching units:', err);
      setError(err.response?.data?.message || 'Failed to fetch units');
    } finally {
      setLoading(false);
    }
  };

  const handleToggleSelect = (unitId: string) => {
    setUnits(prev =>
      prev.map(unit =>
        unit._id === unitId
          ? { ...unit, isSelected: !unit.isSelected }
          : unit
      )
    );
  };

  const handleToggleUpgrade = (unitId: string) => {
    setUnits(prev =>
      prev.map(unit =>
        unit._id === unitId
          ? { ...unit, isUpgrade: !unit.isUpgrade }
          : unit
      )
    );
  };

  const handleCostChange = (unitId: string, value: string) => {
    const numValue = parseFloat(value) || 0;
    setUnits(prev =>
      prev.map(unit =>
        unit._id === unitId
          ? { ...unit, cost: numValue }
          : unit
      )
    );
  };

  const handlePriceChange = (unitId: string, value: string) => {
    const numValue = parseFloat(value) || 0;
    setUnits(prev =>
      prev.map(unit =>
        unit._id === unitId
          ? { ...unit, price: numValue }
          : unit
      )
    );
  };

  const handleApplyBulkPricing = () => {
    const cost = parseFloat(bulkCost) || 0;
    const price = parseFloat(bulkPrice) || 0;

    setUnits(prev =>
      prev.map(unit =>
        unit.isSelected
          ? { ...unit, cost, price }
          : unit
      )
    );
  };

  const handleSave = () => {
    const selectedUnits = units
      .filter(unit => unit.isSelected)
      .map(unit => ({
        unitId: unit._id,
        isUpgrade: unit.isUpgrade,
        cost: unit.cost,
        price: unit.price
      }));

    if (selectedUnits.length === 0) {
      setError('Please select at least one unit');
      return;
    }

    onSave(selectedUnits);
    handleClose();
  };

  const handleClose = () => {
    setUnits([]);
    setSearchTerm('');
    setFilters({ status: '', bed: '', floor: '' });
    setBulkCost('0');
    setBulkPrice('0');
    setError('');
    onClose();
  };

  const handleClearFilters = () => {
    setSearchTerm('');
    setFilters({ status: '', bed: '', floor: '' });
  };

  // Get unique filter options
  const statuses = Array.from(new Set(units.map(u => u.status).filter(Boolean)));
  const beds = Array.from(new Set(units.map(u => u.bed).filter(Boolean)));
  const floors = Array.from(new Set(units.map(u => u.floorMarketing).filter(Boolean)));

  // Filter units based on search and filters
  const filteredUnits = units.filter(unit => {
    const matchesSearch = !searchTerm ||
      unit.unitNo?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      unit.modelName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      unit.status?.toLowerCase().includes(searchTerm.toLowerCase());

    const matchesStatus = !filters.status || unit.status === filters.status;
    const matchesBed = !filters.bed || String(unit.bed) === filters.bed;
    const matchesFloor = !filters.floor || String(unit.floorMarketing) === filters.floor;

    return matchesSearch && matchesStatus && matchesBed && matchesFloor;
  });

  const selectedCount = units.filter(u => u.isSelected).length;

  if (!isOpen) return null;

  return (
    <div className="modal-overlay">
      <div className="add-models-modal-content">
        <div className="modal-header">
          <h3>{editMode ? 'Edit Units' : 'Add Units'}</h3>
          <button className="modal-close-btn" onClick={handleClose}>
            <X size={20} />
          </button>
        </div>

        {/* Filters */}
        <div className="add-models-filters">
          <div className="add-models-filters-row">
            <select
              className="form-input"
              value={filters.status}
              onChange={(e) => setFilters(prev => ({ ...prev, status: e.target.value }))}
            >
              <option value="">Select Status</option>
              {statuses.map(status => (
                <option key={status} value={status}>{status}</option>
              ))}
            </select>

            <select
              className="form-input"
              value={filters.bed}
              onChange={(e) => setFilters(prev => ({ ...prev, bed: e.target.value }))}
            >
              <option value="">Select Beds</option>
              {beds.map(bed => (
                <option key={bed} value={String(bed)}>{bed} Bed{bed !== 1 ? 's' : ''}</option>
              ))}
            </select>

            <select
              className="form-input"
              value={filters.floor}
              onChange={(e) => setFilters(prev => ({ ...prev, floor: e.target.value }))}
            >
              <option value="">Select Floor</option>
              {floors.map(floor => (
                <option key={floor} value={String(floor)}>Floor {floor}</option>
              ))}
            </select>
          </div>

          <div className="add-models-filters-row">
            <input
              type="text"
              className="form-input"
              placeholder="Search"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              style={{ gridColumn: '1 / 3' }}
            />
            <button
              className="btn btn-secondary btn-md"
              onClick={handleClearFilters}
            >
              Clear
            </button>
          </div>
        </div>

        {/* Table */}
        <div className="modal-body">
          {loading ? (
            <div className="add-models-loading">Loading units...</div>
          ) : (
            <div className="add-models-table-wrapper">
              <table className="add-models-table">
                <thead>
                  <tr>
                    <th style={{ width: '50px' }}>
                      <input
                        type="checkbox"
                        onChange={(e) => {
                          const checked = e.target.checked;
                          setUnits(prev => prev.map(u => ({ ...u, isSelected: checked })));
                        }}
                        checked={filteredUnits.length > 0 && filteredUnits.every(u => u.isSelected)}
                        disabled={editMode}
                      />
                    </th>
                    <th>Unit No</th>
                    <th>Floor</th>
                    <th>Model</th>
                    <th>Status</th>
                    <th>Upgrade</th>
                    <th>Cost</th>
                    <th>Price</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredUnits.length === 0 ? (
                    <tr>
                      <td colSpan={8} className="add-models-empty">
                        {editMode
                          ? 'No units have been added to this color collection yet.'
                          : 'No units found matching your filters.'}
                      </td>
                    </tr>
                  ) : (
                    filteredUnits.map(unit => (
                      <tr key={unit._id} className={unit.isSelected ? 'selected' : ''}>
                        <td>
                          <input
                            type="checkbox"
                            checked={unit.isSelected}
                            onChange={() => handleToggleSelect(unit._id)}
                            disabled={editMode}
                          />
                        </td>
                        <td>{unit.unitNo}</td>
                        <td>{unit.floorMarketing || '-'}</td>
                        <td>{unit.modelName || '-'}</td>
                        <td>
                          <span style={{
                            padding: '4px 8px',
                            borderRadius: '4px',
                            fontSize: '11px',
                            fontWeight: '500',
                            backgroundColor:
                              unit.status === 'AVAILABLE' ? '#DEF7EC' :
                              unit.status === 'RESERVED' ? '#FEF3C7' :
                              unit.status === 'SOLD' ? '#FEE2E2' : '#F3F4F6',
                            color:
                              unit.status === 'AVAILABLE' ? '#03543F' :
                              unit.status === 'RESERVED' ? '#92400E' :
                              unit.status === 'SOLD' ? '#991B1B' : '#6B7280'
                          }}>
                            {unit.status || '-'}
                          </span>
                        </td>
                        <td>
                          <label className="switch">
                            <input
                              type="checkbox"
                              checked={unit.isUpgrade}
                              onChange={() => handleToggleUpgrade(unit._id)}
                            />
                            <span className="slider"></span>
                          </label>
                        </td>
                        <td>
                          <input
                            type="number"
                            className="form-input-sm"
                            value={unit.cost}
                            onChange={(e) => handleCostChange(unit._id, e.target.value)}
                            min="0"
                            step="0.01"
                          />
                        </td>
                        <td>
                          <input
                            type="number"
                            className="form-input-sm"
                            value={unit.price}
                            onChange={(e) => handlePriceChange(unit._id, e.target.value)}
                            min="0"
                            step="0.01"
                          />
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="modal-footer">
          <div className="add-models-footer-info">
            <span>Price: </span>
            <input
              type="number"
              className="form-input-sm"
              value={bulkPrice}
              onChange={(e) => setBulkPrice(e.target.value)}
              min="0"
              step="0.01"
            />
          </div>
          <div className="add-models-footer-info">
            <span>Cost: </span>
            <input
              type="number"
              className="form-input-sm"
              value={bulkCost}
              onChange={(e) => setBulkCost(e.target.value)}
              min="0"
              step="0.01"
            />
          </div>
          <button
            className="btn btn-secondary btn-md"
            onClick={handleApplyBulkPricing}
            disabled={selectedCount === 0}
          >
            Apply
          </button>
          <div style={{ flex: 1 }}></div>
          <button
            className="btn btn-secondary btn-md"
            onClick={handleClose}
          >
            Cancel
          </button>
          <button
            className="btn btn-primary btn-md"
            onClick={handleSave}
            disabled={selectedCount === 0 || loading}
          >
            {editMode ? 'Save' : 'Add'} ({selectedCount})
          </button>
        </div>
      </div>
    </div>
  );
};

export default AddUnitsModal;
